import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Mail,
  FileText,
  Calendar,
  FolderOpen,
  Check,
  X,
  Edit3,
  Loader2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  Send,
  Plus,
  RefreshCw,
  Search,
  Download,
} from 'lucide-react';
import { useThemeStore } from '../../store/theme';
import type { ConnectorProvider, ConnectorAction } from '../../types';

interface PendingAction {
  id: number;
  provider: ConnectorProvider;
  providerName: string;
  action: ConnectorAction;
  preview: Record<string, unknown>;
  actionData: Record<string, unknown>;
  requiresApproval: boolean;
}

interface ConnectorActionCardProps {
  action: PendingAction;
  onApprove: (id: number) => void;
  onEdit: (id: number, action: PendingAction) => void;
  onReject: (id: number) => void;
  isLoading?: boolean;
  status?: 'pending' | 'approved' | 'rejected' | 'executed' | 'failed';
  result?: { success: boolean; data?: unknown; error?: string };
}

// Provider icons mapping
const PROVIDER_ICONS: Record<ConnectorProvider, typeof Mail> = {
  gmail: Mail,
  google_docs: FileText,
  google_calendar: Calendar,
  google_drive: FolderOpen,
  trello: FileText, // Using FileText as fallback for Trello
};

// Provider colors
const PROVIDER_COLORS: Record<ConnectorProvider, { bg: string; text: string; border: string }> = {
  gmail: { bg: 'bg-red-500/10', text: 'text-red-500', border: 'border-red-500/30' },
  google_docs: { bg: 'bg-blue-500/10', text: 'text-blue-500', border: 'border-blue-500/30' },
  google_calendar: { bg: 'bg-green-500/10', text: 'text-green-500', border: 'border-green-500/30' },
  google_drive: { bg: 'bg-yellow-500/10', text: 'text-yellow-500', border: 'border-yellow-500/30' },
  trello: { bg: 'bg-indigo-500/10', text: 'text-indigo-500', border: 'border-indigo-500/30' },
};

// Action icons
const ACTION_ICONS: Record<string, typeof Mail> = {
  send_email: Send,
  create_draft: Plus,
  create_event: Plus,
  update_event: RefreshCw,
  create_doc: Plus,
  update_doc: RefreshCw,
  create_card: Plus,
  update_card: RefreshCw,
  move_card: RefreshCw,
  add_comment: Plus,
  search_emails: Search,
  read_emails: Mail,
  read_events: Calendar,
  read_doc: FileText,
  list_files: FolderOpen,
  search_files: Search,
  download_file: Download,
};

// Action labels (human readable)
const ACTION_LABELS: Record<string, string> = {
  send_email: 'Send Email',
  create_draft: 'Create Draft',
  search_emails: 'Search Emails',
  read_emails: 'Read Emails',
  get_labels: 'Get Labels',
  create_event: 'Create Event',
  read_events: 'Read Events',
  update_event: 'Update Event',
  delete_event: 'Delete Event',
  list_calendars: 'List Calendars',
  create_doc: 'Create Document',
  read_doc: 'Read Document',
  update_doc: 'Update Document',
  list_docs: 'List Documents',
  list_files: 'List Files',
  search_files: 'Search Files',
  download_file: 'Download File',
  get_file_info: 'Get File Info',
  create_card: 'Create Card',
  update_card: 'Update Card',
  move_card: 'Move Card',
  add_comment: 'Add Comment',
  list_boards: 'List Boards',
  list_lists: 'List Lists',
  list_cards: 'List Cards',
};

export default function ConnectorActionCard({
  action,
  onApprove,
  onEdit,
  onReject,
  isLoading = false,
  status = 'pending',
  result,
}: ConnectorActionCardProps) {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [isExpanded, setIsExpanded] = useState(false);

  const Icon = PROVIDER_ICONS[action.provider] || Mail;
  const ActionIcon = ACTION_ICONS[action.action] || Send;
  const colors = PROVIDER_COLORS[action.provider] || PROVIDER_COLORS.gmail;
  const actionLabel = ACTION_LABELS[action.action] || action.action;

  // Format preview data for display
  const formatPreviewValue = (key: string, value: unknown): string => {
    if (value === null || value === undefined) return '-';
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    if (typeof value === 'string' && value.length > 100) {
      return value.substring(0, 100) + '...';
    }
    return String(value);
  };

  // Get status badge
  const getStatusBadge = () => {
    switch (status) {
      case 'approved':
      case 'executed':
        return (
          <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-500">
            <Check className="w-3 h-3" />
            {status === 'executed' ? 'Executed' : 'Approved'}
          </span>
        );
      case 'rejected':
        return (
          <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-500">
            <X className="w-3 h-3" />
            Rejected
          </span>
        );
      case 'failed':
        return (
          <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-500/20 text-red-500">
            <X className="w-3 h-3" />
            Failed
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-500/20 text-yellow-500">
            Awaiting Approval
          </span>
        );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl border overflow-hidden ${
        isDark
          ? 'bg-dark-700 border-dark-600'
          : 'bg-white border-slate-200 shadow-sm'
      }`}
    >
      {/* Header */}
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Provider Icon */}
          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors.bg}`}>
            <Icon className={`w-5 h-5 ${colors.text}`} />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                {action.providerName}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${colors.bg} ${colors.text}`}>
                <ActionIcon className="w-3 h-3 inline mr-1" />
                {actionLabel}
              </span>
              {getStatusBadge()}
            </div>

            {/* Preview summary */}
            <div className={`mt-1 text-sm ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
              {action.preview && Object.entries(action.preview).slice(0, 2).map(([key, value]) => (
                <div key={key} className="truncate">
                  <span className="font-medium capitalize">{key.replace(/_/g, ' ')}:</span>{' '}
                  {formatPreviewValue(key, value)}
                </div>
              ))}
            </div>
          </div>

          {/* Expand button */}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`p-1.5 rounded-lg transition-colors ${
              isDark
                ? 'hover:bg-dark-600 text-text-muted'
                : 'hover:bg-slate-100 text-slate-400'
            }`}
          >
            {isExpanded ? (
              <ChevronUp className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
        </div>

        {/* Expanded details */}
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className={`mt-4 pt-4 border-t ${isDark ? 'border-dark-600' : 'border-slate-200'}`}
          >
            <div className={`text-sm space-y-2 ${isDark ? 'text-text-muted' : 'text-slate-600'}`}>
              <p className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Action Details
              </p>
              {action.preview && Object.entries(action.preview).map(([key, value]) => (
                <div key={key} className="flex flex-col gap-1">
                  <span className="font-medium capitalize text-xs">{key.replace(/_/g, ' ')}</span>
                  <pre className={`text-xs whitespace-pre-wrap break-words p-2 rounded ${
                    isDark ? 'bg-dark-800' : 'bg-slate-100'
                  }`}>
                    {formatPreviewValue(key, value)}
                  </pre>
                </div>
              ))}
            </div>

            {/* Result display */}
            {result && (
              <div className={`mt-4 p-3 rounded-lg ${
                result.success
                  ? isDark ? 'bg-green-500/10 border border-green-500/30' : 'bg-green-50 border border-green-200'
                  : isDark ? 'bg-red-500/10 border border-red-500/30' : 'bg-red-50 border border-red-200'
              }`}>
                <p className={`text-sm font-medium ${result.success ? 'text-green-500' : 'text-red-500'}`}>
                  {result.success ? 'Action executed successfully' : 'Action failed'}
                </p>
                {result.error && (
                  <p className={`text-xs mt-1 ${isDark ? 'text-text-muted' : 'text-slate-500'}`}>
                    {result.error}
                  </p>
                )}
                {result.data !== undefined && result.data !== null && (
                  <pre className={`text-xs mt-2 whitespace-pre-wrap break-words ${
                    isDark ? 'text-text-muted' : 'text-slate-600'
                  }`}>
                    {typeof result.data === 'object' ? JSON.stringify(result.data, null, 2) : String(result.data as string)}
                  </pre>
                )}
              </div>
            )}
          </motion.div>
        )}
      </div>

      {/* Action buttons - only show for pending status */}
      {status === 'pending' && (
        <div className={`flex items-center gap-2 p-3 border-t ${
          isDark ? 'border-dark-600 bg-dark-800' : 'border-slate-200 bg-slate-50'
        }`}>
          {/* Approve button */}
          <button
            onClick={() => onApprove(action.id)}
            disabled={isLoading}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              isLoading
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:opacity-90'
            } bg-green-500 text-white`}
          >
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            Approve
          </button>

          {/* Edit button */}
          <button
            onClick={() => onEdit(action.id, action)}
            disabled={isLoading}
            className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              isLoading
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:opacity-90'
            } ${isDark ? 'bg-dark-600 text-white hover:bg-dark-500' : 'bg-white text-slate-700 hover:bg-slate-100 border border-slate-200'}`}
          >
            <Edit3 className="w-4 h-4" />
            Edit
          </button>

          {/* Reject button */}
          <button
            onClick={() => onReject(action.id)}
            disabled={isLoading}
            className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-all ${
              isLoading
                ? 'opacity-50 cursor-not-allowed'
                : 'hover:opacity-90'
            } bg-red-500/10 text-red-500 hover:bg-red-500/20`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </motion.div>
  );
}
